import {
  Box,
  Card,
  VStack,
  Text,
  Button,
  Flex,
  Image,
  Stack,
  HStack,
  Skeleton,
  Popover,
} from '@chakra-ui/react';
import { PopoverContent, PopoverArrow } from '@/components/ui/popover';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TransactionCard, Transaction } from '@/components/transaction/transaction-card';
import { useBalance, MemberStatus } from '@/hooks/wallet/useBalance';
import { useTransactionHistory } from '@/hooks/wallet/useTransactionHistory';
import { toaster } from '@/components/ui/toaster';
import { TransactionDetailSheet } from '@/components/transaction/transaction-detail-sheet';

type UserStatus = 'normal' | 'bronze' | 'silver' | 'gold';

const STATUS_LABEL: Record<UserStatus, string | null> = {
  normal: null,
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
};

const STATUS_BADGE_COLOR: Record<UserStatus, string> = {
  normal: 'teal.600',
  bronze: '#a0522d',
  silver: '#708090',
  gold: '#b8860b',
};

const STATUS_BORDER_COLOR: Record<UserStatus, string | undefined> = {
  normal: undefined,
  bronze: '#a0522d',
  silver: '#708090',
  gold: '#b8860b',
};

const STATUS_GLOW_COLOR: Record<UserStatus, string | undefined> = {
  normal: undefined,
  bronze: 'rgba(160, 82, 45, 0.55)',
  silver: 'rgba(112, 128, 144, 0.55)',
  gold: 'rgba(184, 134, 11, 0.55)',
};

const STATUS_DESCRIPTION: Record<UserStatus, { title: string; body: string } | null> = {
  normal: null,
  bronze: {
    title: 'Bronze Member',
    body: '一定の利用実績に達したメンバーに付与されるステータスです。',
  },
  silver: {
    title: 'Silver Member',
    body: '高い利用実績を持つメンバーに付与されるステータスです。Bronze より多くの特典が得られます。',
  },
  gold: {
    title: 'Gold Member',
    body: '最上位のステータスです。すべての特典を最大限に享受できる最高ランクのメンバーです。',
  },
};

const STATUS_BG_COLOR: Record<UserStatus, string> = {
  normal: 'white/60',
  bronze: '#fdf2e9', // 薄いブロンズ
  silver: '#f4f5f7', // 薄いシルバー
  gold: '#fefae8', // 薄いゴールド
};

const STATUS_GRADIENT: Record<UserStatus, { gradientFrom: string; gradientTo: string }> = {
  normal: { gradientFrom: 'teal.100', gradientTo: 'teal.50' },
  bronze: { gradientFrom: '#d4956a', gradientTo: '#f0d9b5' }, // 薄めの銅色 → 薄い暖色
  silver: { gradientFrom: '#c0c0c0', gradientTo: '#f0f0f0' }, // 銀色 → 薄いシルバー
  gold: { gradientFrom: '#dbb84a', gradientTo: '#fef6d0' }, // 薄めの金色 → 淡い金色
};

const MEMBER_STATUS_MAP: Record<MemberStatus, UserStatus> = {
  NORMAL: 'normal',
  BRONZE: 'bronze',
  SILVER: 'silver',
  GOLD: 'gold',
};

const Home = () => {
  const navigate = useNavigate();

  // useQueryで残高を取得
  const { data: balanceData, isLoading: isLoadingBalance, error: balanceError } = useBalance();

  const userStatus: UserStatus = balanceData?.memberStatus
    ? (MEMBER_STATUS_MAP[balanceData.memberStatus] ?? 'normal')
    : 'normal';
  const statusGradient = STATUS_GRADIENT[userStatus];
  const statusBgColor = STATUS_BG_COLOR[userStatus];
  const statusLabel = STATUS_LABEL[userStatus];
  const statusBadgeColor = STATUS_BADGE_COLOR[userStatus];
  const statusBorderColor = STATUS_BORDER_COLOR[userStatus];
  const statusGlowColor = STATUS_GLOW_COLOR[userStatus];
  const statusDescription = STATUS_DESCRIPTION[userStatus];

  // useQueryで決済履歴を取得
  const {
    data: historyData,
    isLoading: isLoadingHistory,
    error: historyError,
  } = useTransactionHistory();

  const handleScanClick = () => {
    navigate('/main/payments', { state: { openDrawer: 'scan' } });
  };

  const handleMyQRClick = () => {
    navigate('/main/payments', { state: { openDrawer: 'myqr' } });
  };

  const handleChargeClick = () => {
    navigate('/main/profile', { state: { openChargeDrawer: true } });
  };

  // 決済詳細ボトムシート
  const [selectedTransactionCode, setSelectedTransactionCode] = useState<string | null>(null);

  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransactionCode(transaction.code);
  };

  // エラーハンドリング
  useEffect(() => {
    if (balanceError) {
      console.error('残高の取得に失敗しました:', balanceError);
      toaster.create({
        title: '残高の取得に失敗しました',
        description: balanceError.message || '不明なエラーが発生しました',
        type: 'error',
        duration: 1000,
      });
    }
    if (historyError) {
      console.error('決済履歴の取得に失敗しました:', historyError);
      toaster.create({
        title: '決済履歴の取得に失敗しました',
        description: historyError.message || '不明なエラーが発生しました',
        type: 'error',
        duration: 1000,
      });
    }
  }, [balanceError, historyError]);

  const transactions = historyData?.transactions || [];

  return (
    <Box w="full" h="100%" userSelect="none">
      {statusGlowColor && (
        <style>{`
          @keyframes shimmer {
            0%        { transform: translateX(-100%); }
            60%, 100% { transform: translateX(200%); }
          }
        `}</style>
      )}
      <VStack w="full" h="full" gap={6}>
        {/* <Text fontSize="4xl" fontFamily="Comic Sans MS" fontWeight="bold" color="gray.700">
          Let's!{' '}
          <Text as="span" color="teal.600/30" fontSize="4xl" fontWeight="bold">
            BunPay!
          </Text>
        </Text> */}
        <Card.Root
          variant="elevated"
          bg={statusBgColor}
          w="full"
          maxW="450px"
          borderRadius="2xl"
          flexShrink={0}
          border={statusBorderColor ? '0.5px solid' : undefined}
          borderColor={statusBorderColor}
        >
          <Card.Body py={6} overflow="hidden" position="relative">
            {statusLabel && statusDescription && (
              <Popover.Root positioning={{ placement: 'bottom-end' }}>
                <Popover.Trigger asChild>
                  <Box
                    position="absolute"
                    top={3}
                    right={4}
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    border="1px solid"
                    borderColor={statusBadgeColor}
                    cursor="pointer"
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color={statusBadgeColor}
                      letterSpacing="wider"
                    >
                      {statusLabel}
                    </Text>
                  </Box>
                </Popover.Trigger>
                <PopoverContent maxW="260px" p={4}>
                  <PopoverArrow />
                  <Popover.Body p={0}>
                    <Text fontSize="sm" fontWeight="bold" color={statusBadgeColor} mb={1}>
                      {statusDescription.title}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {statusDescription.body}
                    </Text>
                  </Popover.Body>
                </PopoverContent>
              </Popover.Root>
            )}
            <Box mb={2}>
              <Text fontSize="sm" color="gray.500" mb={-1}>
                残高
              </Text>
              <Skeleton loading={isLoadingBalance} height="2.5rem" width="fit-content" minW="200px">
                <Text fontSize="3xl" fontWeight="bold" color="teal.600">
                  {balanceData?.balance.toLocaleString() || '--'}{' '}
                  <Text as="span" fontSize="lg" color="gray.500">
                    Bun
                  </Text>
                </Text>
              </Skeleton>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">
                再チャージまで
              </Text>
              <HStack gap={2} align="center">
                <Text fontSize="md" fontWeight="bold" color="teal.600">
                  <Text as="span" fontSize="sm" color="teal.600">
                    残り
                  </Text>{' '}
                  --{' '}
                  <Text as="span" fontSize="sm" color="gray.500">
                    日
                  </Text>
                </Text>
                <Button
                  size="xs"
                  borderRadius="lg"
                  borderColor="teal.400"
                  colorPalette="teal"
                  variant="outline"
                  onClick={handleChargeClick}
                  px={3}
                  py={1}
                  h="auto"
                >
                  チャージ
                </Button>
              </HStack>
            </Box>
            <Image
              src="/img/BunPay-logo-sample4.png"
              alt="main logo"
              w="48"
              position="absolute"
              bottom="-3"
              right="-5"
              opacity="0.1"
              pointerEvents="none"
            />
          </Card.Body>
          <Card.Footer
            bgGradient="to-br"
            gradientFrom={statusGradient.gradientFrom}
            gradientTo={statusGradient.gradientTo}
            borderBottomRadius="2xl"
            justifyContent="space-around"
            py={4}
            px={6}
            position="relative"
            overflow="hidden"
          >
            {statusGlowColor && (
              <Box
                position="absolute"
                top={0}
                left={0}
                w="full"
                h="full"
                background="linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)"
                css={{ animation: 'shimmer 4s ease-in-out infinite' }}
                pointerEvents="none"
              />
            )}
            <Button borderRadius="3xl" w="40%" colorPalette="teal" onClick={handleScanClick}>
              スキャン
            </Button>
            <Button borderRadius="3xl" w="40%" colorPalette="teal" onClick={handleMyQRClick}>
              マイQR
            </Button>
          </Card.Footer>
        </Card.Root>
        <Flex w="100vw" flex="1" gap={4} minH={0}>
          <Card.Root variant="elevated" borderTopRadius="4xl" w="full" h="full" bg="white/70">
            <Card.Body pt={6} pb={4} px={6}>
              <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={4}>
                決済履歴
              </Text>
              <Box
                overflowY="auto"
                maxH="calc(100vh - 500px)"
                pb={12}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#CBD5E0',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#A0AEC0',
                  },
                }}
              >
                {isLoadingHistory ? (
                  <Stack gap={3}>
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} height="60px" borderRadius="lg" />
                    ))}
                  </Stack>
                ) : transactions.length === 0 ? (
                  <Text color="gray.500" textAlign="center" py={8}>
                    取引履歴がありません。
                  </Text>
                ) : (
                  <Stack gap={3}>
                    {transactions.map(transaction => (
                      <TransactionCard
                        key={transaction.code}
                        transaction={transaction}
                        onClick={handleTransactionClick}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Card.Body>
          </Card.Root>
        </Flex>
      </VStack>
      {/* 決済詳細ボトムシート */}
      <TransactionDetailSheet
        transactionCode={selectedTransactionCode}
        isOpen={!!selectedTransactionCode}
        onClose={() => setSelectedTransactionCode(null)}
      />
    </Box>
  );
};

export default Home;
